import React, { useCallback, useState } from "react";
import { Footer, Nav, SnackbarContainer } from "./components";
import { useAuth } from "./contexts/AuthContext";
import { SnackbarProvider } from "./contexts/SnackbarContext";
import { onSignInExpired } from "./lib/api";
import { PAGE_OWNER, canView, destinationAfterLogin, hashPath, homeFor, isDashboardPage, pageName } from "./lib/routes";

import { AdminDashboard } from "./pages/AdminDashboard";
import { BookingModal } from "./pages/BookingModal";
import { HowItWorksPage, PricingPage } from "./pages/Courses";
import { HomePage } from "./pages/Home";
import {
  AboutPage,
  ApplyToTeachPage,
  BlogPage,
  BlogPostPage,
  ContactPage,
  InstructorFAQPage,
  PayoutInfoPage,
} from "./pages/Info";
import { InstructorDashboard } from "./pages/InstructorDashboard";
import { LoginPage } from "./pages/LoginPage";
import { InstructorProfilePage, MarketplacePage } from "./pages/Marketplace";
import { RegisterPage } from "./pages/RegisterPage";
import { StudentDashboard } from "./pages/StudentDashboard";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { SetPasswordPage } from "./pages/SetPasswordPage";
import { MfaSetupPage } from "./pages/MfaSetupPage";
import { StopEmailsPage } from "./pages/StopEmailsPage";
import { PayResultPage } from "./pages/PayResultPage";

const VALID_PAGES = [
  "home",
  "marketplace",
  "instructor-profile",
  "pricing",
  "how-it-works",
  "about",
  "blog",
  "contact",
  "dashboard-student",
  "dashboard-instructor",
  "admin",
  "apply-to-teach",
  "instructor-faq",
  "payout-info",
  "login",
  "register",
  "verify-email",
  "set-password",
  "mfa-setup",
  "stop-emails",
  // Back from JTPay's pay page (JTP-20). main.jsx hands /pay/result to it; student-only (PAGE_OWNER).
  "pay-result",
];

function pageFromHash(hash) {
  if (!hash || hash === "#/" || hash === "#" || hash === "") return "home";
  let path = hash.replace("#/", "");
  // Strip any query string (e.g. #/verify-email?token=...) before matching the route.
  const qIndex = path.indexOf("?");
  if (qIndex !== -1) path = path.slice(0, qIndex);
  if (path.startsWith("blog-post-")) return path;
  if (path.startsWith("instructor-profile-")) return path;
  if (VALID_PAGES.includes(path)) return path;
  return "home";
}

function parseHash() {
  return pageFromHash(window.location.hash);
}

function App() {
  const { user, isAuthenticated, isStudent, isInstructor, isAdmin, logout, loading } = useAuth();
  const [page, setPage] = useState(parseHash);
  const [currency, setCurrency] = useState("₱");
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [booking, setBooking] = useState(null);
  // Where to send someone once they sign in, and why they were sent to the login page at all.
  // Set when a sign-in expires mid-task (TTT-80).
  const [afterLogin, setAfterLogin] = useState(null);
  const [loginNotice, setLoginNotice] = useState("");

  const authState = !isAuthenticated
    ? "loggedout"
    : isAdmin
      ? "admin"
      : isStudent
        ? "student"
        : isInstructor
          ? "instructor"
          : "loggedout";

  // `sessions` is one session or an array — students can book several at once.
  const openBooking = (instructor, sessions) => {
    const picked = { instructor, sessions: Array.isArray(sessions) ? sessions : [sessions] };
    if (!isAuthenticated) {
      // Keep what they picked rather than throwing it away (TTT-82). Signing in brings them
      // back here with these sessions still selected. `page` (not a hardcoded page name) because
      // an instructor's profile is now `instructor-profile-<slug>` (TTT-86) — hardcoding the bare
      // name would send them back to the wrong instructor's page after signing in.
      setAfterLogin({ page, booking: picked });
      setLoginNotice(
        `Log in to book with ${instructor?.displayName || "this instructor"}. ` +
          `Your ${picked.sessions.length === 1 ? "session is" : "sessions are"} waiting for you.`,
      );
      navigateTo("login");
      return;
    }
    setBooking(picked);
  };
  const closeBooking = () => setBooking(null);

  const navigateTo = useCallback(p => {
    if (typeof p !== "string") {
      console.error("[Router] navigateTo expected a string, got:", p);
      return;
    }
    // A query (e.g. `dashboard-student?review=<id>`, kept through sign-in) is not part of the page name.
    const name = pageName(p);
    const isValid = VALID_PAGES.includes(name) || name.startsWith("blog-post-") || name.startsWith("instructor-profile-");
    if (
      !isValid &&
      (window.location.hostname === "localhost" || window.location.search.includes("debug=1"))
    ) {
      console.error("[Router] Unrecognized route:", p);
    }
    const targetHash = "#/" + p;
    if (window.location.hash !== targetHash) {
      window.location.hash = targetHash;
    } else {
      setPage(pageFromHash(targetHash));
      window.scrollTo(0, 0);
    }
  }, []);

  React.useEffect(() => {
    const onHashChange = () => {
      const p = parseHash();
      setPage(p);
      window.scrollTo(0, 0);
    };
    window.addEventListener("hashchange", onHashChange);
    const initial = parseHash();
    if (initial !== page) setPage(initial);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigateTo("home");
  };

  // Hands a freshly signed-in user to their destination: the page an expired sign-in
  // interrupted, or their own dashboard.
  const handleAuthenticated = useCallback(
    signedIn => {
      const intent = afterLogin;
      setAfterLogin(null);
      setLoginNotice("");

      const next = destinationAfterLogin(intent, signedIn.role);
      if (next.booking) {
        setSelectedInstructor(next.booking.instructor?.id ?? null);
        setBooking(next.booking);
      }
      navigateTo(next.page);
    },
    [afterLogin, navigateTo],
  );

  // The API client renews an expired access token on its own. This fires only when renewing
  // failed too — the sign-in is genuinely over, so say so and take them somewhere they can act.
  React.useEffect(
    () =>
      onSignInExpired(() => {
        // A logged-out visitor's `/auth/me` 401 reaches here too. There was no sign-in to lose,
        // so nothing should move — otherwise every first-time visitor lands on the login page.
        if (!isAuthenticated) return;
        if (page === "login" || page === "register") return;
        // The booking pop-up is fixed-position and lives outside the router, so leaving it open
        // would cover the login page it is sending them to.
        setBooking(null);
        setAfterLogin({ page });
        setLoginNotice("Your sign-in expired. Log in to continue.");
        navigateTo("login");
      }),
    [isAuthenticated, page, navigateTo],
  );

  // Leaving the login page abandons whatever sent them there, so it cannot resurface later.
  //
  // Driven by each hashchange's own URL rather than by the rendered page: two navigations in
  // quick succession collapse into a single render, and a check against render state would miss
  // the page in between — leaving a stale booking to pop open on a later, unrelated sign-in.
  React.useEffect(() => {
    const onHashChange = e => {
      if (pageFromHash(new URL(e.newURL).hash) === "login") return;
      setAfterLogin(null);
      setLoginNotice("");
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const isDashboard = isDashboardPage(page);
  const showFooter = !isDashboard;

  // Role-based route guards.
  //
  // `loading` is the whole ballgame on a reload: AuthProvider starts at user=null while it asks
  // the API who this is, and React runs this child effect before that request is even sent. Act
  // on that first render and a signed-in instructor reloading their own dashboard is thrown at
  // the login page — then never sent back, because by the time they are known the page is
  // 'login' and no role owns it.
  React.useEffect(() => {
    if (loading) return;
    if (!PAGE_OWNER[page]) return;
    if (authState === 'loggedout') {
      // Remember the whole address, query included, so signing in returns there. The review email
      // links to `#/dashboard-student?review=<id>`; dropping the query would lose the review form.
      //
      // An expired sign-in gets here too: it saved the page and moved the address to the login
      // page before this render. Saving now would replace that page with the login page, and
      // signing in again would land on login with the button stuck on "Signing in…" (#500).
      const here = hashPath(window.location.hash);
      if (pageName(here) !== 'login') setAfterLogin(saved => saved ?? { page: here });
      navigateTo('login');
      return;
    }
    if (!canView(authState, page)) navigateTo(homeFor(authState));
  }, [page, authState, loading]);

  const isBlogPost = page.startsWith("blog-post-");
  const blogPostId = isBlogPost ? page.replace("blog-post-", "") : null;

  const renderPage = () => {
    if (page === "home")
      return (
        <HomePage
          setPage={navigateTo}
        />
      );
    if (page === "marketplace")
      return (
        <MarketplacePage
          setPage={navigateTo}
        />
      );
    if (page === "instructor-profile" || page.startsWith("instructor-profile-"))
      return (
        <InstructorProfilePage
          instructorId={
            page.startsWith("instructor-profile-")
              ? page.replace("instructor-profile-", "")
              : selectedInstructor || "mari"
          }
          setPage={navigateTo}
          openBooking={openBooking}
        />
      );
    if (page === "pricing") return <PricingPage setPage={navigateTo} />;
    if (page === "how-it-works") return <HowItWorksPage setPage={navigateTo} />;
    if (page === "about") return <AboutPage setPage={navigateTo} />;
    if (page === "blog") return <BlogPage setPage={navigateTo} />;
    if (isBlogPost)
      return (
        <BlogPostPage
          postId={blogPostId}
          setPage={navigateTo}
          setSelectedInstructor={setSelectedInstructor}
        />
      );
    if (page === "contact") return <ContactPage setPage={navigateTo} />;
    if (page === "dashboard-student")
      return (
        <StudentDashboard setPage={navigateTo} />
      );
    if (page === "dashboard-instructor") return <InstructorDashboard setPage={navigateTo} />;
    if (page === "admin") return <AdminDashboard setPage={navigateTo} />;
    if (page === "apply-to-teach") return <ApplyToTeachPage setPage={navigateTo} />;
    if (page === "instructor-faq") return <InstructorFAQPage setPage={navigateTo} />;
    if (page === "payout-info") return <PayoutInfoPage setPage={navigateTo} />;
    if (page === "login")
      return <LoginPage setPage={navigateTo} onAuthenticated={handleAuthenticated} notice={loginNotice} />;
    if (page === "register") return <RegisterPage setPage={navigateTo} />;
    if (page === "verify-email") return <VerifyEmailPage setPage={navigateTo} />;
    if (page === "set-password") return <SetPasswordPage setPage={navigateTo} />;
    if (page === "mfa-setup") return <MfaSetupPage setPage={navigateTo} />;
    // The stop link from a "Notify me" email (#/stop-emails?token=...). Public, and it stops
    // only when the student presses Stop emails, never on load.
    if (page === "stop-emails") return <StopEmailsPage setPage={navigateTo} />;
    if (page === "pay-result") return <PayResultPage setPage={navigateTo} />;
    return (
      <HomePage
        setPage={navigateTo}
      />
    );
  };

  return (
    <SnackbarProvider>
      <div style={{ minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {!isDashboard && (
          <Nav
            page={page}
            setPage={navigateTo}
            authState={authState}
            onLogout={handleLogout}
            currency={currency}
            setCurrency={setCurrency}
          />
        )}

        <main>{renderPage()}</main>

        {showFooter && <Footer setPage={navigateTo} />}

        {booking && (
          <BookingModal
            instructor={booking.instructor}
            sessions={booking.sessions}
            onClose={closeBooking}
            onConfirm={() => {
              closeBooking();
              navigateTo("dashboard-student");
            }}
          />
        )}

        <SnackbarContainer />
      </div>
    </SnackbarProvider>
  );
}

export default App;
